<template>
  <div class="terms">
    <div class="header mt-2">
      <div />
      <div class="h1 flex justify-content-center">
        Risk Disclosure Statement
      </div>
      <div class="flex justify-content-end">
        <AuButton
          center
          width="100px"
          @click="openDoc"
        >
          Download
        </AuButton>
      </div>
    </div>
    <div class="h5 mt-5 ms-4 me-4">
      <p><strong>Version:</strong> 4.2 (Property Investment Crowdfunding — DIFC)</p>
      <p><strong>Last Updated:</strong> 2.02.2026</p>
      <p><strong>Platform:</strong> RWA.capital (operated by [Legal Entity Name], DIFC)</p>
      <p><strong>Regulatory status:</strong> DFSA authorization application in progress.</p>

      <h3 class="mt-4">
        1. General Risk Warning
      </h3>
      <p>
        Investment through the Platform is speculative and implies high-risk.
        You may lose part or all of your money and/or experience delays in
        receiving payments.
      </p>
      <p>
        This statement is a general summary only and shall not be considered
        exhaustive. It is neither investment, nor legal, nor tax advice. Retail
        Clients shall constitute the primary audience. Professional/Qualified
        Investors may be onboarded via a separate process, and may be offered
        regulatory protections of a different level.
      </p>
      <p>
        Borrowed funds: employing loans or borrowed funds for investment entails
        greater risk since repayments will have to be done even in the event the
        investment value drops or returns/payments are delayed.
      </p>

      <h3 class="mt-4">
        2. No Guarantee of Income or Capital Preservation
      </h3>
      <p>
        No guarantee: target yields, valuations and forecasts are of indicative
        nature only. Rental income, resale value and distributions shall not be
        guaranteed.
      </p>
      <p>
        Capital at risk: property prices may drop whereas costs may rise. In
        case of adverse scenarios (vacancy, tenant default, market downturn,
        disputes, uninsured events), your entire investment may be lost.
      </p>

      <h3 class="mt-4">
        3. Type of Investment (SPV/Indirect Ownership)
      </h3>
      <p>
        Indirect ownership: you will not receive a title deed. What you acquire
        is shares/units in a Special Purpose Vehicle (SPV) where the main asset
        shall be the property (or rights to it).
      </p>
      <p>
        Control and voting: as a rule, you are to be a minority holder.
        Investors may vote on certain reserved matters (as defined in the
        SPV/Offering documents), whereas day-to-day decisions shall be delegated
        to duly appointed managers/service providers.
      </p>
      <p>
        No leverage policy: the SPV is not intended to use borrowed funds. Any
        deviation (if ever permitted) shall be disclosed and, where applicable,
        shall be subject to required approvals.
      </p>

      <h3 class="mt-4">
        4. Liquidity and Exit Risks
      </h3>
      <p>
        Illiquid investment: interests in the SPVs are non-listed securities and
        may involve certain constraints when attempting to liquidate them at a
        preferred time or at the anticipated price.
      </p>
      <p>
        Exit Window: if offered, transfer opportunities are expected twice per
        year and depend on buyers' demand, KYC/AML checks and on approvals to be
        duly obtained. No guarantee of sale, timing or price shall be expected
        here.
      </p>
      <p>
        End-of-term sale risk: the property may be difficult to sell at the end
        of the investment period, this resulting in delays and/or a sale at a
        loss.
      </p>

      <h3 class="mt-4">
        5. Real Estate and Local Market Risks (Dubai/UAE)
      </h3>
      <p>
        Vacancy and tenant risk: the property may remain vacant or tenants may
        default, whereas service charges, maintenance and insurance will be
        running.
      </p>
      <p>
        Valuation and cost risk: valuations are opinions and may bear
        substantial difference from the eventual sale price.
        Transaction/transfer costs and unexpected repairs may affect returns
        either reducing them or eliminating entirely.
      </p>
      <p>
        Legal/regulatory restrictions: zoning, licensing, ownership and right
        transfer restrictions (including for foreign owners) may apply, which
        may affect the value and exit.
      </p>
      <p>
        Strategy-specific risk: Fix&Flip/Development projects imply risks of
        permits to be obtained, construction delays, cost overruns, contractor
        default and failure to complete the project or sell it on time.
      </p>
      <p>
        Currency risk: investments/distributions may be done in AED and/or USD;
        exchange rate fluctuations and conversion costs may reduce returns.
      </p>

      <h3 class="mt-4">
        6. Platform, Operational and Technology Risks
      </h3>
      <p>
        Operational dependency: administration, reporting and distributions rely
        on the Platform and third-party service providers; errors, outages or
        delays may occur.
      </p>
      <p>
        Information reliability: data may be sourced from third parties and may
        be incomplete or inaccurate. You shall be held responsible for your own
        due diligence.
      </p>
      <p>
        Business cessation: in the event the Platform operator ceases business,
        investors may incur costs and/or experience delays in communications,
        administration or payments.
      </p>

      <h3 class="mt-4">
        7. Fees, Costs and Conflicts of Interest
      </h3>
      <p>
        Fees and expenses reduce returns and may be payable even in case of poor
        performance. Unless the Offering states otherwise, the expected fee
        framework shall be as indicated below:
      </p>
      <ul>
        <li><strong>Entry Fee (Investor):</strong> 1.5% of investment amount (one-off at subscription/investment).</li>
        <li><strong>KYC/AML (Investor):</strong> 0.2% at entry + 0.1% per year from Year 2 on.</li>
        <li><strong>Annual Administration (SPV):</strong> 0.5% per year (charged on a quarterly basis from the rental income).</li>
        <li><strong>Property Management (SPV):</strong> 5% (from the rental income).</li>
        <li><strong>AUM Fee (SPV):</strong> 0.5% per year (from the rental income).</li>
        <li><strong>Exit Fee (Selling Investor):</strong> 2.5% of the sale price (on transfer/exit).</li>
        <li><strong>Transfer Fee:</strong> payable on transfer of shares/units (the rate to be disclosed in the Fee Schedule/Offering).</li>
        <li><strong>Performance Fee (Selling Investor):</strong> 7% of the actually obtained profit on capital appreciation (only in the event there is profit).</li>
        <li><strong>Capital Raising Fee (Asset Owner):</strong> 6% of the capital raised successfully via the Platform.</li>
      </ul>
      <p>
        Conflicts: the Platform and/or affiliates may receive fees while
        performing within multiple roles. Respective details are to be disclosed
        in the Offering documents.
      </p>
      <p>
        By proceeding, you confirm you have read and are aware of the provisions
        of this Risk Disclosure Statement, as well as you accept all the risks
        described above.
      </p>
    </div>
  </div>
</template>

<script setup>
import AuButton from "@/components/extended/AuButton.vue";
import { checkAccess, scope } from "@/help/accessData";
import { onMounted } from "vue";
import { useRouter } from "vue-router";

const router = useRouter();
const openDoc = () => {
  window.open("/docs/Risk_disclosure_statement.pdf", "_blank");
};
onMounted(() => {
  if (checkAccess(scope.notRwa)) {
    router.push({ name: "page not found" });
  }
});
</script>

<style scoped lang="scss">
.terms {
  background-color: var(--main-color);
  padding-bottom: 10px;

  .header {
    display: grid;
    grid-template-columns: 33% auto 33%;
    padding-right: 20px;
  }
}

a {
  color: var(--color-green);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>